import React, { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Button, Card, Typography, Space, message } from "antd"
import { ArrowLeftOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { ServiceReviewsViewModel } from "./ServiceReviewsViewModel"
import { ServiceReview, ServiceReviewUpdate } from "../../../domain/entities/ServiceReview"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { AuthStore } from "../../stores/AuthStore"
import ServiceReviewsTable from "./components/ServiceReviewsTable"
import ServiceReviewModal from "./components/ServiceReviewModal"
import { FormData, ModalMode, LocationState } from "./components/interface/ServiceReviewsInterfaces"

const { Title } = Typography

export const ServiceReviewsView: React.FC = observer(() => {
	const { t } = useTranslation("serviceReviews")
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as LocationState | null

	const serviceId = state?.serviceId || new URLSearchParams(location.search).get("serviceId")
	const viewModel = useContainerInjection<ServiceReviewsViewModel>("ServiceReviewsViewModel")
	const { userData } = useContainerInjection<AuthStore>("AuthStore")

	const [showModal, setShowModal] = useState(false)
	const [modalMode, setModalMode] = useState<ModalMode>("create")
	const [editingReview, setEditingReview] = useState<ServiceReview | null>(null)
	const [formData, setFormData] = useState<FormData>({
		review: "",
		rating: 0,
		userId: undefined,
		userName: "",
		userEmail: ""
	})

	useEffect(() => {
		if (serviceId) {
			viewModel.fetchServiceReviews(parseInt(serviceId), state?.serviceName)
		} else {
			message.error(t("messages.noServiceIdProvided"))
			navigate(-1)
		}
	}, [serviceId, viewModel, state?.serviceName, navigate, t])

	const handleEditReview = (record: ServiceReview) => {
		setModalMode("edit")
		setEditingReview(record)
		setFormData({
			review: record.review || "",
			rating: record.rating || 0,
			userId: record.user?.id,
			userName: record.user?.name || "",
			userEmail: record.user?.email || ""
		})
		setShowModal(true)
	}

	const handleSaveReview = async () => {
		try {
			if (!formData.review || formData.rating === 0) {
				message.error(t("messages.requiredFields"))
				return
			}

			if (modalMode === "edit" && editingReview) {
				if (!formData.userId) {
					message.error(t("messages.userRequired"))
					return
				}

				const reviewUpdate: ServiceReviewUpdate = {
					id: editingReview.id,
					userId: formData.userId,
					review: formData.review,
					rating: formData.rating
				}

				const userInfo = {
					name: formData.userName,
					email: formData.userEmail
				}

				await viewModel.updateReview(reviewUpdate, userInfo)
				message.success(t("messages.updateSuccess"))
			} else {
				if (!formData.userId) {
					message.error(t("messages.userRequired"))
					return
				}

				await viewModel.createReview({
					userId: formData.userId,
					serviceId: parseInt(serviceId!),
					review: formData.review,
					rating: formData.rating,
					userName: formData.userName,
					userEmail: formData.userEmail
				})

				message.success(t("messages.createSuccess"))
			}
			handleCloseModal()
		} catch (error) {
			message.error(modalMode === "edit" ? t("messages.updateError") : t("messages.createError"))
		}
	}

	const handleCloseModal = () => {
		setShowModal(false)
		setModalMode("create")
		setEditingReview(null)
		setFormData({
			review: "",
			rating: 0,
			userId: undefined,
			userName: "",
			userEmail: ""
		})
	}

	const handleCreateReview = () => {
		setModalMode("create")
		setEditingReview(null)
		setFormData({
			review: "",
			rating: 0,
			userId: undefined,
			userName: "",
			userEmail: ""
		})
		setShowModal(true)
	}

	const handleDeleteReview = async (reviewId: number) => {
		try {
			await viewModel.deleteReview(reviewId)
			message.success(t("messages.deleteSuccess"))
		} catch (error) {
			message.error(t("messages.deleteError"))
		}
	}

	const handleSearchUsers = (search: string) => {
		viewModel.searchUsers(search)
	}

	const userOptions = React.useMemo(() => {
		const searchedUserOptions = viewModel.searchedUsers.map(user => ({
			value: parseInt(user.id || "0"),
			label: user.name || user.userName || "Usuario sin nombre",
			email: user.email
		}))

		// Si estamos editando una review, asegurar que el usuario actual esté en la lista
		if (modalMode === "edit" && editingReview?.user) {
			const currentUserId = editingReview.user.id
			const isCurrentUserInList = searchedUserOptions.some(option => option.value === currentUserId)

			if (!isCurrentUserInList) {
				const currentUserOption = {
					value: currentUserId,
					label: editingReview.user.name || "Usuario sin nombre",
					email: editingReview.user.email || ""
				}
				return [currentUserOption, ...searchedUserOptions]
			}
		}

		return searchedUserOptions
	}, [viewModel.searchedUsers, modalMode, editingReview])

	const handleBack = () => {
		navigate(-1)
	}

	return (
		<div style={{ padding: "24px" }}>
			<Card>
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
							<Button icon={<ArrowLeftOutlined />} onClick={handleBack} type="text">
								{t("backToServices")}
							</Button>
							<Title level={3} style={{ margin: 0 }}>
								{t("reviewsOf")} {viewModel.serviceName}
							</Title>
						</div>
						<Button type="primary" onClick={handleCreateReview}>
							{t("addReview")}
						</Button>
					</div>

					<ServiceReviewsTable
						viewModel={viewModel}
						onEditReview={handleEditReview}
						onDeleteReview={handleDeleteReview}
					/>
				</Space>
			</Card>

			<ServiceReviewModal
				open={showModal}
				mode={modalMode}
				formData={formData}
				isLoading={viewModel.isLoading}
				users={userOptions}
				isLoadingUsers={viewModel.isLoadingUsers}
				onSave={handleSaveReview}
				onCancel={handleCloseModal}
				onFormChange={setFormData}
				onSearchUsers={handleSearchUsers}
			/>
		</div>
	)
})

export default ServiceReviewsView
